import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import {
    getAppMode,
    getLocalDateTime,
    getRandomNumber,
    getRandomString,
    isRealApp,
} from 'app/shared/utils/comUtil'
import IbkHeader from 'app/core/models/base/ibk-header'
import {
    commonDialog,
    commonProgressDialog,
} from 'app/shared/context/CommonUIProvider'
import { showAlert } from 'app/shared/utils/util'
import { RootState } from 'app/core/store'
import { comUtil } from 'app/shared/utils'

const baseApiUrl = process.env.REACT_APP_API_URL
const appMode = getAppMode()
const isReal = isRealApp()
const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    CustomExtraOptions
> = (args, api, extraOptions) => {
    const targetBaseQuery = fetchBaseQuery({
        baseUrl: baseApiUrl,
        prepareHeaders: (headers) => {
            const csrfToken = comUtil.getUserData('tkn', false)
            const screId = window.location.pathname.slice(1) // 화면번호
            const rqstYmd = getLocalDateTime('YYYYMMDD')
            const rqstHms = getLocalDateTime('hhmmssn')
            const trnRqstTs = rqstYmd + rqstHms
            let sysEnvrDcd = 'D'

            const typeCode = args['url']?.includes('com/ath') ? '2' : '3'

            if (appMode === 'production') sysEnvrDcd = 'P'
            else if (appMode === 'test') sysEnvrDcd = 'T'

            // 글로벌ID 채번 규칙 : 전송일자(8) + 망구분(1) + 시스템코드(3) + 업무구분코드(3) + 일련번호(1) + 전송일시(9) + 랜덤시퀀스(5) + 진행구분번호(4) = 34 byte
            const guid =
                rqstYmd +
                sysEnvrDcd +
                'UIB' +
                'MAO' +
                typeCode +
                rqstHms +
                getRandomNumber(5) +
                getRandomString(4)

            const reqHeader: IbkHeader = {
                guid: guid, // GUID
                traceId: '', // 트레이스ID
                rqstSysDcd: 'UIB', // 요청시스템구분코드
                rqstSysBswrDcd: 'MAO', // 요청시스템업무구분코드
                clntIp: '', // 클라이언트IP     - 공백 고정
                clntMac: '', // 클라이언트MAC   - 공백 고정
                usrIdntNo: '', // 사용자ID     - 공백 고정
                sysEnvrDcd: sysEnvrDcd, // 시스템환경구분코드
                trnRqstTs: trnRqstTs, // 거래요청일시
                trnMctm: '', // 거래유지시간
                screId: screId.split('/').includes('gw')
                    ? 'gw'
                    : screId.split('/').pop(), // 화면번호
                lnggDcd: 'ko', // 언어구분코드
                tkn: csrfToken, // 토큰
            }

            headers.set(
                'X-Ibk-Systemheader',
                window.btoa(JSON.stringify(reqHeader))
            )

            return headers
        },
    })

    return targetBaseQuery(args, api, extraOptions)
}

// CASE1
// "error": {
//     "status": 500,
//     "data": {
//     "timeStamp": 1753176472321,
//     "path": "/uib/mao/prd/dep/dep02/dep0202",
//     "status": 500,
//     "reason": "Internal Server Error",
//     "exctMsg": "ibk.bxcm.core.ext.exception.IbkApplicationException",
//     "msgCd": "E30006",
//     "bscMsg": "세션정보가 유효하지 않습니다. "
// }
// }
// CASE2
// "error" {
//     "data": {
//         "timeStamp" : 1748317756487,
//         "path" : "/uib/mao/com/ath/ath0301",
//         "status" : 404,
//         "reason" : "Not Found",
//         "exctMsg" : "org.springframework.web.servlet.resource.NoResourceFoundException",
//         "bscMsg" : "No static resource uib/mao/com/ath/ath0301."
//     }
// }

type ComptError = {
    path?: string
    reason?: string
    status?: number | string
    timestamp?: number
    exctMsg?: string
    msgCd?: string
    bscMsg?: string
}

type ErrorData = Omit<FetchBaseQueryError, 'data'> & {
    data?: ComptError
}

interface CustomExtraOptions {
    /**
     * 에러에 대해 화면에서 처리할지 여부, true: 화면에서 처리 false or undefined: 공통부에서 처리
     * 공통에러(서버 장애 등 화면에서 처리불가능한)인 경우에는 true여도 여기에서 처리한다.
     */
    handleLocally?: boolean
    /**
     * API로딩에 프로그레스 로딩을 보여줄지 여부. false: 보여줌, true: 보이지 않음
     * 페이지 검증 API에서는 보여주지 않는다.(화면 전환 로딩과 중복 표시됨)
     */
    skipProgress?: boolean
}

const blackErrorCodeList = [
    'E30006', // 세션정보가 유효하지 않습니다.
    'E31044', // 세션정보가 유효하지 않습니다.
    'E31045', // 세션정보가 유효하지 않습니다.
    'E39995', // 잘못된 유입경로입니다. IBK큐브 메인 화면으로 이동합니다.
    'BXCME00053',
    'BXCME00054',
    'BXCME00055',
    'BXCME00056',
    'BXCME00057',
    'BXCME00058',
    'BXCME00059',
    'BXCME00060',
    'BXCME00061',
    'BXCME00062',
    'BXCME00063',
    'BXCME00064',
    'BXCME00065',
    'BXCME00066',
    'BXCME00067',
    'BXCME00068',
    'BXCME00069',
    'BXCME00070',
]

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    CustomExtraOptions
> = async (args, api, extraOptions) => {
    let hideLoading = () => {}

    const globalLoading = comUtil.getUserData('globalLoading', false) ?? true

    if (!extraOptions?.skipProgress && globalLoading) {
        //로딩을 보여주지 말아야하는 경우이다.
        hideLoading = commonProgressDialog()
    }

    const result = await baseQuery(args, api, extraOptions)
    hideLoading()

    if (result.error) {
        const error = result.error as ErrorData
        /** STATUS 가 500이 아니면 orginalStatus 확인, 아니면 서버오류가 아님 */
        if (
            Number(error.status) !== 500 &&
            !window.location.pathname.includes('/auth')
        ) {
            if (
                error.status === 'PARSING_ERROR' &&
                error['originalStatus'] === 500 &&
                (error['data'] as string).match(
                    /[R|r]edis|springframework\.dao|QueryTimeoutException/g
                )
            ) {
                window.location.replace('/auth/error')
            } else {
                window.location.replace('/auth/fail')
            }
            return
        } else {
            /** 서버 오류 시작 */
            if (!error.data.msgCd) {
                //메세지코드 없는 경우...
                showAlert({
                    msg: error.data?.bscMsg ?? '',
                    onConfirm: () => window.location.replace('/'),
                })
                const returnVal = {
                    ...error,
                    data: { bscMsg: '' },
                } as FetchBaseQueryError
                return { ...result, error: returnVal }
            } else if (blackErrorCodeList.includes(error.data.msgCd)) {
                /** 발생하면 무조건 팅겨야하는 에러코드 */
                showAlert({
                    msg: error.data.bscMsg,
                    onConfirm: () => window.location.replace('/'),
                })
                const returnVal = {
                    ...error,
                    data: { bscMsg: '' },
                } as FetchBaseQueryError
                return { ...result, error: returnVal }
            } else {
                if (!extraOptions?.handleLocally) {
                    showAlert({
                        msg: error.data?.bscMsg ?? '',
                        onConfirm: () => window.location.replace('/'),
                    })
                }
            }
        }
    }

    return result
}

const baseQueryWithRetry = retry(baseQueryWithReauth, { maxRetries: 0 })

export const api = createApi({
    reducerPath: 'splitApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Post', 'Counter', 'Posts', 'Cluster', 'Sys'],
    endpoints: () => ({}),
})
